/**
 * The work log.
 *
 * One row per *run of consecutive reasoning and tool calls*, not one row per
 * part. This is T3's `WorkLog.tsx` and it is structural, not cosmetic: a real
 * turn in this app produced 3000 parts and 2027 disclosures, and rendering each
 * as its own `<details>` rebuilt ~30k DOM nodes on every streamed token. Grouping
 * collapses that to a handful of rows.
 *
 * The disclosure state lives here, in React, and *not* in the DOM. That matters:
 * the previous client recorded intent on a synchronous `click` because the
 * transcript was rebuilt wholesale and an async `toggle` event arrived after the
 * tapped node had already been replaced. React owns the node identity, so the
 * old hazard is gone — but the state still lives above the transcript so a
 * re-render cannot lose it.
 */

import { memo, useMemo, useState } from "react";
import type { MessagePart, ToolPart } from "../lib/models";
import { toolLabel } from "../lib/models";
import { IconChevronRight } from "./icons";

/** A reasoning part or a tool call, in transcript order. */
export interface WorkItem {
  key: string;
  part: MessagePart;
}

/**
 * Collapse a part list into renderable runs.
 *
 * Text, images, errors and input prompts break a run: they are the content the
 * user is reading, and a tool call either side of them is not one continuous
 * activity. Reasoning and tool calls join the run.
 */
export type Block =
  | { kind: "text"; key: string; part: MessagePart }
  | { kind: "other"; key: string; part: MessagePart }
  | { kind: "work"; key: string; items: WorkItem[]; errors: number };

export function toBlocks(parts: readonly MessagePart[], entryKey: string): Block[] {
  const blocks: Block[] = [];
  let run: WorkItem[] = [];
  let errors = 0;

  const flush = (): void => {
    if (run.length === 0) return;
    const first = run[0];
    if (first === undefined) return;
    blocks.push({ kind: "work", key: `${entryKey}:work:${first.key}`, items: run, errors });
    run = [];
    errors = 0;
  };

  parts.forEach((part, index) => {
    const key = `${entryKey}:${index}`;
    if (part.kind === "reasoning" || part.kind === "tool") {
      run.push({ key, part });
      if (part.kind === "tool" && part.isError === true) errors += 1;
      return;
    }
    flush();
    if (part.kind === "text") blocks.push({ kind: "text", key, part });
    else blocks.push({ kind: "other", key, part });
  });
  flush();
  return blocks;
}

/**
 * "Thinking, Bash ×40, Read ×80 — 3 failed".
 *
 * Exported because it is the one string that tells the user what a collapsed run
 * did, and it is worth testing directly.
 */
export function workLabel(items: readonly WorkItem[], errors: number): string {
  let thinking = false;
  const counts = new Map<string, number>();
  for (const item of items) {
    if (item.part.kind === "reasoning") {
      thinking = true;
      continue;
    }
    if (item.part.kind !== "tool") continue;
    // The call's own name groups better than `toolLabel`, which appends the
    // argument and would make every invocation its own distinct entry.
    const call = item.part.call ?? {};
    const raw = typeof call.name === "string" && call.name.length > 0 ? call.name : "tool";
    // A subagent spawn is named `Agent: <description>`; the description belongs
    // on the chip, not in the summary. Counting them as `Agent ×3` keeps the
    // summary about *what kind* of work happened.
    const name = raw === "Agent" || raw.startsWith("Agent: ") ? "Agent" : raw;
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  const named = [...counts.entries()]
    .map(([name, count]) => (count > 1 ? `${name} \u00d7${count}` : name))
    .join(", ");
  const label = thinking && named.length > 0 ? `Thinking, ${named}` : thinking ? "Thinking" : named;
  if (label.length === 0) return "Work";
  return errors > 0 ? `${label} \u2014 ${errors} failed` : label;
}

/**
 * One run of work, collapsed to a single 24px row until opened.
 *
 * `memo` with a key built from the run's own contents: a streamed token changes
 * the *last* run only, and every earlier run in a long transcript is byte-identical
 * and skips rendering. That is what keeps a 3000-part turn cheap.
 */
export const WorkRow = memo(
  function WorkRow({ items, errors }: { items: WorkItem[]; errors: number }) {
    const [open, setOpen] = useState(false);
    const label = useMemo(() => workLabel(items, errors), [items, errors]);
    return (
      <div className={errors > 0 ? "text-error-foreground" : undefined}>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex min-h-6 w-full items-center gap-1 rounded-md px-0.5 text-left text-sm text-muted-foreground hover:text-foreground"
        >
          <IconChevronRight
            className={`size-3.5 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
          />
          <span className="min-w-0 truncate">{label}</span>
        </button>
        {open ? (
          <div className="mt-1 mb-1 flex flex-col gap-0.5 border-l border-border pl-3">
            {items.map((item) => (
              <WorkItemRow key={item.key} item={item} />
            ))}
          </div>
        ) : null}
      </div>
    );
  },
  (previous, next) =>
    previous.errors === next.errors &&
    previous.items.length === next.items.length &&
    previous.items[previous.items.length - 1]?.part === next.items[next.items.length - 1]?.part
);

/** One tool call or reasoning block inside an opened run. */
function WorkItemRow({ item }: { item: WorkItem }) {
  const part = item.part;
  if (part.kind === "reasoning") {
    return (
      <p className="py-0.5 text-sm whitespace-pre-wrap text-muted-foreground italic">
        {part.text}
      </p>
    );
  }
  if (part.kind !== "tool") return null;
  return <ToolRow part={part} />;
}

/**
 * A tool call.
 *
 * Subagent spawns arrive as an unknown tool named `Agent: <description>` — every
 * native driver's convention, per the engine's `view.rs` — so they get their own
 * treatment rather than being lumped in with extension tools.
 */
function ToolRow({ part }: { part: ToolPart }) {
  const [open, setOpen] = useState(false);
  const call = part.call ?? {};
  const rawName = typeof call.name === "string" ? call.name : "";
  const isSubagent = rawName === "Agent" || rawName.startsWith("Agent: ");
  const label = isSubagent ? rawName.replace(/^Agent:?\s*/, "") || "Subagent" : toolLabel(part);
  // `toolLabel` already appends the interesting argument (a command, a path) to
  // the name, so the whole label is the detail line — there is no separate field.
  const detail = label === rawName ? "" : label.replace(`${rawName}: `, "");
  const hasBody = typeof part.output === "string" && part.output.length > 0;
  // A subagent is a tool call the engine tracks separately: it has its own
  // status and a live tail of the child's output. The desktop shows both on the
  // chip, so a running child is visible without opening anything.
  const subagentStatus = part.subagentStatus ?? null;
  const subagentTail = part.subagentTail ?? null;

  return (
    <div>
      <button
        type="button"
        onClick={() => hasBody && setOpen((value) => !value)}
        aria-expanded={hasBody ? open : undefined}
        className={`flex min-h-6 w-full items-center gap-1.5 rounded-md px-0.5 text-left text-sm ${
          part.isError === true ? "text-error-foreground" : "text-muted-foreground"
        } ${hasBody ? "hover:text-foreground" : "cursor-default"}`}
      >
        {isSubagent ? (
          <span className="shrink-0 rounded bg-accent px-1.5 py-px text-2xs font-medium text-accent-foreground">
            agent
          </span>
        ) : null}
        <span className="shrink-0 font-medium">{isSubagent ? label : rawName || "tool"}</span>
        {isSubagent ? null : <span className="min-w-0 truncate">{detail}</span>}
        {subagentStatus === "running" ? (
          <span className="shrink-0 text-2xs text-info">running…</span>
        ) : subagentStatus === "failed" ? (
          <span className="shrink-0 text-2xs text-error-foreground">failed</span>
        ) : subagentStatus === "done" ? (
          <span className="shrink-0 text-2xs text-success-foreground">done</span>
        ) : part.resolved === false ? (
          <span className="shrink-0 text-2xs text-muted-foreground">running…</span>
        ) : null}
      </button>
      {subagentTail !== null && subagentTail.length > 0 ? (
        <p className="mt-0.5 mb-0.5 truncate border-l border-border pl-2 font-mono text-2xs text-muted-foreground">
          {subagentTail}
        </p>
      ) : null}
      {open && hasBody ? (
        <pre className="mt-1 mb-1 max-h-80 overflow-auto rounded-md bg-code p-2 font-mono text-xs whitespace-pre-wrap text-code-foreground">
          {part.output}
        </pre>
      ) : null}
    </div>
  );
}
